import styles from './CheckboxToggleCell.module.scss';

import { CheckCircleRounded, CircleTwoTone } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Row } from 'react-table';

import { useTicketSolutionDialog } from '@work4all/components/lib/components/entity-preview/ticket-preview/ticket-solution-dialog/use-ticket-solution-dialog';

import { useDataMutation, useUser } from '@work4all/data';
import { settings, useSetting } from '@work4all/data/lib/settings';

import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { TicketStatus } from '@work4all/models/lib/Enums/TicketStatus.enum';
import { ITableColumnConfig } from '@work4all/models/lib/table-schema/table-schema';

import { isHTMLEmpty } from '@work4all/utils';
import { canEditTicket } from '@work4all/utils/lib/permissions';

export type ITicketStatusCellProps = {
  value: TicketStatus;
  row: Row<Ticket>;
  column: ITableColumnConfig;
};

export const TicketStatusCell = (props: ITicketStatusCellProps) => {
  const {
    value,
    row: { original: ticket },
    column: { id: statusKey },
  } = props;

  const { t } = useTranslation();

  const user = useUser();
  const isAllowedToModify = canEditTicket(user, ticket);
  const toggleStatus = useEditTicketStatus(statusKey);

  const Icon =
    value === TicketStatus.ERLEDIGT ? CheckCircleRounded : CircleTwoTone;

  return (
    <div className={styles['task-status-cell-root']}>
      <IconButton
        disabled={!isAllowedToModify}
        sx={{ padding: 0 }}
        onClick={(e) => {
          e.stopPropagation();
          toggleStatus(ticket);
        }}
      >
        <Icon
          className={clsx(styles['check-circle'], {
            [styles['check-circle-success']]: value === TicketStatus.ERLEDIGT,
            [styles['check-circle-edit']]:
              value === TicketStatus.IN_BEARBEITUNG ||
              value === TicketStatus.ZURUECKGESTELLT,
          })}
        />
      </IconButton>

      <span className={styles['label']}>{t(`TICKET_STATUS.${value}`)}</span>
    </div>
  );
};

export const useEditTicketStatus = (statusKey: string) => {
  const [mutate] = useDataMutation({
    entity: Entities.ticket,
    mutationType: EMode.upsert,
    responseData: { id: null, [statusKey]: null },
    resetStore: false,
  });

  const user = useUser();
  const isSolutionRequired = useSetting(settings.isTicketSolutionRequiered());
  const ticketDialog = useTicketSolutionDialog();

  const toggleStatus = async (
    ticket: Ticket,
    status: TicketStatus = TicketStatus.ERLEDIGT
  ) => {
    const isAllowedToModify = canEditTicket(user, ticket);

    const isDone = ticket[statusKey] === TicketStatus.ERLEDIGT;

    if (!isAllowedToModify) return;
    if (isDone) {
      mutate({ id: ticket.id, [statusKey]: status ?? TicketStatus.OFFEN });
    } else {
      if (
        isSolutionRequired.value &&
        !ticket.solutionText &&
        status === TicketStatus.ERLEDIGT
      ) {
        const solutionText = await ticketDialog();
        if (!isHTMLEmpty(solutionText))
          mutate({
            id: ticket.id,
            [statusKey]: TicketStatus.ERLEDIGT,
            solutionText,
          });

        return;
      }
      mutate({ id: ticket.id, [statusKey]: status ?? TicketStatus.ERLEDIGT });
    }
  };
  return toggleStatus;
};
